import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "ranging_review_filters"

// Client-side filtering for ranging review stock rows.
// Filters by search text, brand, PH1, PH2, PH3 dropdowns and Core/NPD/Focus checkboxes.
// Dropdowns cascade: brand constrains PH1, PH1 constrains PH2, PH2 constrains PH3.
// Filter state persists in localStorage so it carries over when switching between responses.
export default class extends Controller {
  static targets = ["searchFilter", "searchClear", "brandFilter", "ph1Filter", "ph2Filter", "ph3Filter", "coreFilter", "npdFilter", "focusFilter", "phoneFocusFilter", "row"]

  connect() {
    this.cacheSelectOptions()
    this.restoreFilters()
    this.cascade()
    this.filter()
  }

  // Store the full set of options for each select so we can restore them during cascade
  cacheSelectOptions() {
    this.allOptions = {}
    const selects = { brand: "brandFilter", ph1: "ph1Filter", ph2: "ph2Filter", ph3: "ph3Filter" }
    for (const [key, targetName] of Object.entries(selects)) {
      const hasTarget = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTarget]) {
        const select = this[`${targetName}Target`]
        this.allOptions[key] = Array.from(select.options).map(o => ({ value: o.value, text: o.text }))
      }
    }
  }

  // Cascade: rebuild lower-level selects based on what rows match upper-level filters
  cascade() {
    const rows = this.rowTargets

    // Determine which values exist at each level given current upper-level selections
    const brandId = this.hasBrandFilterTarget ? this.brandFilterTarget.value : ""

    // Filter rows matching brand (for PH1 cascade)
    const brandRows = brandId ? rows.filter(r => r.dataset.brandId === brandId) : rows
    this.rebuildSelect("ph1", "ph1Filter", brandRows, "ph1Id")

    // Filter rows matching brand + PH1 (for PH2 cascade)
    const ph1Id = this.hasPh1FilterTarget ? this.ph1FilterTarget.value : ""
    const ph1Rows = ph1Id ? brandRows.filter(r => r.dataset.ph1Id === ph1Id) : brandRows
    this.rebuildSelect("ph2", "ph2Filter", ph1Rows, "ph2Id")

    // Filter rows matching brand + PH1 + PH2 (for PH3 cascade)
    const ph2Id = this.hasPh2FilterTarget ? this.ph2FilterTarget.value : ""
    const ph2Rows = ph2Id ? ph1Rows.filter(r => r.dataset.ph2Id === ph2Id) : ph1Rows
    this.rebuildSelect("ph3", "ph3Filter", ph2Rows, "ph3Id")
  }

  rebuildSelect(optionKey, targetName, matchingRows, dataAttr) {
    const hasTarget = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
    if (!this[hasTarget] || !this.allOptions[optionKey]) return

    const select = this[`${targetName}Target`]
    const currentVal = select.value

    // Collect valid IDs from matching rows
    const validIds = new Set()
    matchingRows.forEach(r => {
      const id = r.dataset[dataAttr]
      if (id) validIds.add(id)
    })

    // Rebuild options: keep blank + only those that exist in matching rows
    const savedOptions = this.allOptions[optionKey]
    select.innerHTML = ""
    savedOptions.forEach(opt => {
      if (opt.value === "" || validIds.has(opt.value)) {
        const o = new Option(opt.text, opt.value)
        select.add(o)
      }
    })

    // Restore selection if still valid, otherwise reset
    if (validIds.has(currentVal)) {
      select.value = currentVal
    } else {
      select.value = ""
    }
  }

  clearSearch() {
    if (this.hasSearchFilterTarget) {
      this.searchFilterTarget.value = ""
      this.filter()
    }
  }

  filter() {
    const searchTerm = this.hasSearchFilterTarget ? this.searchFilterTarget.value.toLowerCase().trim() : ""
    if (this.hasSearchClearTarget) {
      this.searchClearTarget.classList.toggle("hidden", !searchTerm)
    }
    const brandId = this.hasBrandFilterTarget ? this.brandFilterTarget.value : ""
    const ph1Id = this.hasPh1FilterTarget ? this.ph1FilterTarget.value : ""
    const ph2Id = this.hasPh2FilterTarget ? this.ph2FilterTarget.value : ""
    const ph3Id = this.hasPh3FilterTarget ? this.ph3FilterTarget.value : ""
    const coreOnly = this.hasCoreFilterTarget && this.coreFilterTarget.checked
    const npdOnly = this.hasNpdFilterTarget && this.npdFilterTarget.checked
    const focusOnly = this.hasFocusFilterTarget && this.focusFilterTarget.checked
    const phoneFocusOnly = this.hasPhoneFocusFilterTarget && this.phoneFocusFilterTarget.checked

    this.cascade()
    this.saveFilters({ search: searchTerm, brandId, ph1Id, ph2Id, ph3Id, coreOnly, npdOnly, focusOnly, phoneFocusOnly })

    this.rowTargets.forEach(row => {
      const matchesSearch = !searchTerm ||
        (row.dataset.productName && row.dataset.productName.includes(searchTerm)) ||
        (row.dataset.productSku && row.dataset.productSku.includes(searchTerm))
      const matchesBrand = !brandId || row.dataset.brandId === brandId
      const matchesPh1 = !ph1Id || row.dataset.ph1Id === ph1Id
      const matchesPh2 = !ph2Id || row.dataset.ph2Id === ph2Id
      const matchesPh3 = !ph3Id || row.dataset.ph3Id === ph3Id
      const matchesCore = !coreOnly || row.dataset.core === "true"
      const matchesNpd = !npdOnly || row.dataset.npd === "true"
      const matchesFocus = !focusOnly || row.dataset.focus === "true"
      const matchesPhoneFocus = !phoneFocusOnly || row.dataset.phoneFocus === "true"

      const visible = matchesSearch && matchesBrand && matchesPh1 && matchesPh2 && matchesPh3 && matchesCore && matchesNpd && matchesFocus && matchesPhoneFocus
      row.closest("turbo-frame").classList.toggle("hidden", !visible)
    })
  }

  saveFilters(state) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch (_) { /* quota exceeded */ }
  }

  restoreFilters() {
    let state
    try { state = JSON.parse(localStorage.getItem(STORAGE_KEY)) } catch (_) { return }
    if (!state) return

    if (state.search && this.hasSearchFilterTarget) this.searchFilterTarget.value = state.search
    if (state.brandId && this.hasBrandFilterTarget) this.brandFilterTarget.value = state.brandId
    if (state.ph1Id && this.hasPh1FilterTarget) this.ph1FilterTarget.value = state.ph1Id
    if (state.ph2Id && this.hasPh2FilterTarget) this.ph2FilterTarget.value = state.ph2Id
    if (state.ph3Id && this.hasPh3FilterTarget) this.ph3FilterTarget.value = state.ph3Id
    if (this.hasCoreFilterTarget) this.coreFilterTarget.checked = !!state.coreOnly
    if (this.hasNpdFilterTarget) this.npdFilterTarget.checked = !!state.npdOnly
    if (this.hasFocusFilterTarget) this.focusFilterTarget.checked = !!state.focusOnly
    if (this.hasPhoneFocusFilterTarget) this.phoneFocusFilterTarget.checked = !!state.phoneFocusOnly
  }
}
